import React, { useEffect } from 'react';
import type { AccessibilityRole, Role } from 'react-native';
import { t } from '@lingui/core/macro';

import KEYS from '@rover/constants/src/platform/keys.constants';
import Check from '@rover/icons/svg/check.svg';
import type { SvgIcon } from '@rover/types/src/SvgIcon';
import { Flex } from '@rover/kibble/core';
import type { SizeSystemType, SpacingSystemType } from '@rover/kibble/core/styledSystemPropTypes';
import {
  FeedbackMessage,
  FeedbackMessageProps,
  PrimaryLabel,
  SecondaryLabel,
} from '@rover/kibble/forms/fields/sharedTypography';
import { isReactNative } from '@rover/rsdk/src/modules/Network/userAgent';

import type { ChipSize, ChipType } from '..';
import FilterChip, { type OptionProps } from '../Chip';

/**
 * Use this component to render a group of filter chips that can be selected or deselected,
 * and which has an immediate effect on another part of the page via onChange.
 *
 * This component should NOT be used as a form input where the value is submitted elsewhere.
 * For that, please use (or create) a chip variant of RadioField or CheckboxField.
 */

type FilterChipGroupProps = {
  groupLabel: string;
  groupSecondaryLabel?: string;
  showLabel?: boolean;
  options: OptionProps[];
  canSelectMultiple: boolean;
  isDeselectable?: boolean; // Whether selected chips can be deselected without selecting a new one
  // When true, each chip stays an individual Tab stop so Tab moves between chips
  // (the ARIA checkbox-group pattern). When false (default), the group behaves as
  // a single composite widget: Tab exits it and arrow keys move between chips.
  tabbableChips?: boolean;
  size?: ChipSize;
  selectedIcon?: SvgIcon | null; // Optional icon to display on selected chip(s)
  selectedValues?: Array<string | number>;
  separatorIndices?: number[];
  shouldWrap?: boolean;
  onChange: (newValue: Array<string | number> | (string | number) | null) => void;
  nativePlatform?: 'android' | 'ios';
  chipGap?: SpacingSystemType;
  chipPaddingX?: SpacingSystemType | [SpacingSystemType, SpacingSystemType];
  chipPaddingY?: SpacingSystemType | [SpacingSystemType, SpacingSystemType];
  chipWidth?: SizeSystemType;
  fontWeight?: 'regular' | 'semibold';
  fieldError?: FeedbackMessageProps['fieldError'];
};

const WebSeparatorHeightMap: Record<ChipSize, string> = {
  small: '30px',
  default: '44px',
};
const calculateSeparatorHeight = (s: ChipSize): string =>
  isReactNative() ? '100%' : WebSeparatorHeightMap[s];

const FilterChipGroup: React.FC<FilterChipGroupProps> = ({
  groupLabel,
  groupSecondaryLabel,
  showLabel = true,
  options,
  canSelectMultiple,
  isDeselectable = canSelectMultiple,
  tabbableChips = false,
  size = 'default',
  selectedIcon = Check,
  selectedValues = [],
  separatorIndices = [],
  shouldWrap = true,
  onChange,
  nativePlatform,
  chipGap = '2x',
  chipPaddingX,
  chipPaddingY,
  chipWidth,
  fontWeight,
  fieldError,
}) => {
  const [currentSelections, setCurrentSelections] = React.useState<boolean[]>(
    options.map(({ value }) => selectedValues.includes(value))
  );
  const [chipRefs, setChipRefs] = React.useState<Array<React.RefObject<ChipType>>>([]);
  const [tabIndex, setTabIndex] = React.useState<number>(0);

  if (canSelectMultiple && !isDeselectable) {
    throw new Error(
      'FilterChipGroup with "canSelectMultiple" true must also have "isDeselectable" true.'
    );
  }

  useEffect(() => {
    // if currentSelections changes, fire onChange with the new selected values
    if (canSelectMultiple) {
      onChange(options.filter((_, idx) => currentSelections[idx]).map((opt) => opt.value));
    } else {
      onChange(options[currentSelections.indexOf(true)]?.value ?? null);
    }
  }, [currentSelections, canSelectMultiple, options, onChange]);

  useEffect(() => {
    // Initialize refs for each chip
    setChipRefs((prevRefs) =>
      options.map((_, idx) => prevRefs[idx] || React.createRef<ChipType>())
    );
  }, [options]);

  const handleClick = (optionIndex: number): void => {
    if (canSelectMultiple) {
      setCurrentSelections(currentSelections.map((val, idx) => (idx === optionIndex ? !val : val)));
    } else {
      setCurrentSelections(
        Array.from({ length: currentSelections.length }, (_, idx) =>
          isDeselectable && currentSelections[optionIndex] ? false : idx === optionIndex
        )
      );
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    // Find all chip buttons within this fieldset
    const fieldset = event.currentTarget;
    const chipButtons = Array.from(
      fieldset.querySelectorAll('[data-testid="kibble-patterns-chip"]')
    ) as HTMLButtonElement[];
    const focusedChipIndex = chipButtons.findIndex((button) => button === document.activeElement);

    if (focusedChipIndex === -1) return;

    switch (event.key) {
      case KEYS.DOWN:
      case KEYS.RIGHT:
        if (focusedChipIndex < chipButtons.length - 1) {
          event.preventDefault();
          chipButtons[focusedChipIndex + 1]?.focus();
        }
        break;

      case KEYS.UP:
      case KEYS.LEFT:
        if (focusedChipIndex > 0) {
          event.preventDefault();
          chipButtons[focusedChipIndex - 1]?.focus();
        }
        break;

      case KEYS.SPACE:
      case KEYS.ENTER:
        // Don't prevent the default behavior
        break;

      case KEYS.TAB:
        // With `tabbableChips`, leave every chip at tabIndex 0 so Tab moves between
        // chips one at a time (each is its own Tab stop). Otherwise drop all chips
        // out of the tab order so Tab exits the group as a single composite widget,
        // then restore tabIndex once focus has left so it can return later.
        if (!tabbableChips) {
          setTabIndex(-1);
          setTimeout(() => setTabIndex(0), 100);
        }
        break;

      default:
        event.preventDefault();
        break;
    }
  };

  const isCheckbox = canSelectMultiple || isDeselectable;
  const chipRole = isCheckbox ? 'checkbox' : 'radio';
  const webA11yProps = {
    role: isCheckbox ? 'group' : 'radiogroup',
  };
  const rxnA11yProps = {
    // there's no 'checkboxgroup' AccessibiltyRole, nor even a 'group' role, so we use 'checkbox' :wtf:
    accessibilityRole: (isCheckbox ? 'checkbox' : 'radiogroup') as AccessibilityRole,
    // but Role has a 'group'. What's the difference? ¯\_ (ツ)_/¯
    role: (isCheckbox ? 'group' : 'radiogroup') as Role,
  };

  // iOS RxN doesn't announce these roles via accessibility APIs, so we do it manually via the hint
  const roleA11yHint = isCheckbox ? t`checkbox` : t`radio button`;
  // and then add the group label if it's hidden, since iOS doesn't focus the group either
  const iOSA11yHint = showLabel ? roleA11yHint : `${groupLabel}, ${roleA11yHint}`;

  return (
    <Flex
      webAs="fieldset"
      rxnProps={rxnA11yProps}
      webProps={webA11yProps}
      aria-label={showLabel ? undefined : groupLabel}
      aria-labelledby={showLabel ? 'filter-chip-group-label-id' : undefined}
      flexDirection="column"
      margin="0x"
      mb="4x"
      padding="0x"
      border="none"
      data-testid="filter-chip-group-fieldset"
      webAsProps={{
        onKeyDown: handleKeyDown,
      }}
    >
      {showLabel && (
        <PrimaryLabel
          as="legend"
          id="filter-chip-group-label-id"
          primaryLabel={groupLabel}
          required={false}
        />
      )}
      {groupSecondaryLabel && <SecondaryLabel secondaryLabel={groupSecondaryLabel} />}
      {fieldError && (
        <Flex mb="3x">
          <FeedbackMessage fieldError={fieldError} />
        </Flex>
      )}
      <Flex
        alignItems="center"
        sx={{ flexFlow: `row ${shouldWrap ? 'wrap' : ''}`, gap: chipGap }}
        data-testid="filter-chip-group"
      >
        {options.map((option, idx) => (
          <React.Fragment key={option.id}>
            <FilterChip
              key={option.id}
              chipRef={chipRefs[idx]}
              {...option}
              icon={option.icon}
              isSelected={currentSelections[idx]}
              selectedIcon={selectedIcon ?? undefined}
              role={chipRole}
              size={size}
              tabIndex={tabIndex}
              onClick={() => handleClick(idx)}
              accessibilityHint={nativePlatform === 'ios' ? iOSA11yHint : undefined}
              paddingX={chipPaddingX}
              paddingY={chipPaddingY}
              width={chipWidth}
              fontWeight={fontWeight}
            />

            {separatorIndices.includes(idx) && (
              <Flex
                width="1px"
                height={calculateSeparatorHeight(size)}
                borderColor="separator.primary"
                borderRight="1px solid"
                borderRightWidth="1px" // RxN needs this defined independently of `borderRight`
                mx="2x"
                data-testid="kibble-patterns-chip-separator"
              />
            )}
          </React.Fragment>
        ))}
      </Flex>
    </Flex>
  );
};

export default FilterChipGroup;
